import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import logo from "../traderhub24-logo.svg";
import Footer from "./components/Footer";

function RiskWarning({ onBack, onNavigate }: { onBack: () => void; onNavigate?: (page: "home" | "changelog" | "privacy-policy" | "terms-and-conditions" | "risk-warning" | "documentation" | "contact") => void }) {
  const navigate = useNavigate();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return (
    <div className="min-h-screen bg-app-bg text-gray-900">
      {/* Header */}
      <header className="w-full py-3 px-4 md:px-6 max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <img 
            src={logo} 
            alt="TraderHub24" 
            className="h-12 md:h-20 w-auto cursor-pointer hover:opacity-80 transition-opacity" 
            onClick={onBack}
          />
          <button
            onClick={onBack}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Back
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 md:px-6 py-6">
        <div className="bg-app-box shadow-sm border border-gray-200 rounded-2xl p-4 md:p-8">
          <h1 className="text-2xl md:text-3xl font-bold mb-2">Risk Warning</h1>
          <p className="text-gray-600 mb-8">
            Last Updated: 2025-12-17
          </p>

          <div className="prose prose-gray max-w-none space-y-6">
            <p className="text-gray-700 leading-relaxed">
              Trading and investing in financial markets involve substantial risk and may not be suitable for all individuals.
            </p>
            <p className="text-gray-700 leading-relaxed">
              By using this TraderHub24, you acknowledge and accept the risks described below.
            </p>

            <section className="mt-8">
              <h2 className="text-2xl font-semibold mb-4 text-gray-900">1. No Financial Advice</h2>
              <p className="text-gray-700 mb-2">TraderHub24 does not provide:</p>
              <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                <li>Investment advice</li>
                <li>Trading recommendations</li>
                <li>Personalized financial guidance</li>
              </ul>
              <p className="text-gray-700 mt-2">
                All tools, analytics, metrics, and content are provided for informational and educational purposes only.
              </p>
              <p className="text-gray-700">
                Any trading decision you make is solely your responsibility.
              </p>
            </section>

            <section className="mt-8">
              <h2 className="text-2xl font-semibold mb-4 text-gray-900">2. Risk of Financial Loss</h2>
              <p className="text-gray-700 mb-2">
                Trading financial instruments such as stocks, cryptocurrencies, derivatives, or CFDs may result in:
              </p>
              <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                <li>Partial or total loss of capital</li>
                <li>Losses exceeding your initial investment</li>
                <li>Rapid and unexpected price movements</li>
              </ul>
              <p className="text-gray-700 mt-2">
                You should only trade with capital you can afford to lose.
              </p>
            </section>

            <section className="mt-8">
              <h2 className="text-2xl font-semibold mb-4 text-gray-900">3. Market Volatility</h2>
              <p className="text-gray-700 mb-2">Financial markets can be:</p>
              <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                <li>Highly volatile</li>
                <li>Influenced by macroeconomic events, news, and sentiment</li>
                <li>Subject to sudden gaps, slippage, or illiquidity</li>
              </ul>
              <p className="text-gray-700 mt-2">
                Past performance or historical data does not guarantee future results.
              </p>
            </section>

            <section className="mt-8">
              <h2 className="text-2xl font-semibold mb-4 text-gray-900">4. Accuracy of Data and Calculations</h2>
              <p className="text-gray-700 mb-2">While we strive to provide accurate and reliable information:</p>
              <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                <li>Market data may be delayed, incomplete, or inaccurate</li>
                <li>Calculations, indicators, and analytics may contain errors</li>
                <li>System outputs should not be relied upon as a sole decision-making basis</li>
              </ul>
              <p className="text-gray-700 mt-2">
                You are responsible for verifying any information before acting on it.
              </p>
            </section>

            <section className="mt-8">
              <h2 className="text-2xl font-semibold mb-4 text-gray-900">5. Technical and Operational Risks</h2>
              <p className="text-gray-700 mb-2">Use of TraderHub24 involves technical risks, including:</p>
              <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                <li>Platform downtime or service interruptions</li>
                <li>Connectivity or hardware failures</li>
                <li>Software bugs or calculation issues</li>
              </ul>
              <p className="text-gray-700 mt-2">
                We are not responsible for losses caused by technical or operational failures.
              </p>
            </section>

            <section className="mt-8">
              <h2 className="text-2xl font-semibold mb-4 text-gray-900">6. Third-Party Risks</h2>
              <p className="text-gray-700 mb-2">
                TraderHub24 may rely on third-party data providers, brokers, or integrations.
              </p>
              <p className="text-gray-700 mb-2">We do not control and are not responsible for:</p>
              <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                <li>Third-party systems</li>
                <li>External data accuracy</li>
                <li>Actions or failures of third-party services</li>
              </ul>
            </section>

            <section className="mt-8">
              <h2 className="text-2xl font-semibold mb-4 text-gray-900">7. Regulatory Differences</h2>
              <p className="text-gray-700 mb-2">Financial regulations vary by jurisdiction.</p>
              <p className="text-gray-700 mb-2">TraderHub24 does not guarantee compliance with:</p>
              <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                <li>Local trading regulations</li>
                <li>Tax reporting requirements</li>
                <li>Broker-specific rules</li>
              </ul>
              <p className="text-gray-700 mt-2">
                You are responsible for understanding and complying with applicable laws in your jurisdiction.
              </p>
            </section>

            <section className="mt-8">
              <h2 className="text-2xl font-semibold mb-4 text-gray-900">8. Psychological and Behavioral Risks</h2>
              <p className="text-gray-700 mb-2">Trading may lead to:</p>
              <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                <li>Emotional stress</li>
                <li>Overtrading</li>
                <li>Behavioral biases and impulsive decisions</li>
              </ul>
              <p className="text-gray-700 mt-2">
                Risk management tools reduce risk but do not eliminate it.
              </p>
            </section>

            <section className="mt-8">
              <h2 className="text-2xl font-semibold mb-4 text-gray-900">9. No Guarantees</h2>
              <p className="text-gray-700 mb-2">TraderHub24 makes no guarantees regarding:</p>
              <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                <li>Profitability</li>
                <li>Performance improvement</li>
                <li>Trading success</li>
              </ul>
              <p className="text-gray-700 mt-2">
                Any examples, simulations, or performance metrics are hypothetical and for illustrative purposes only.
              </p>
            </section>

            <section className="mt-8">
              <h2 className="text-2xl font-semibold mb-4 text-gray-900">10. Acceptance of Risk</h2>
              <p className="text-gray-700 mb-2">By using TraderHub24, you confirm that:</p>
              <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                <li>You understand the risks involved</li>
                <li>You assume full responsibility for your trading activity</li>
                <li>You release TraderHub24 from liability related to trading outcomes</li>
              </ul>
              <p className="text-gray-700 mt-4 font-semibold">
                If you do not agree with these risks, do not use TraderHub24.
              </p>
            </section>
          </div>
        </div>
      </main>

      {/* Footer */}
      <Footer currentPage="risk-warning" onNavigate={onNavigate || ((page) => {
        if (page === "risk-warning") return;
        else if (page === "privacy-policy") navigate("/privacy");
        else if (page === "terms-and-conditions") navigate("/terms");
        else if (page === "documentation") navigate("/documentation");
        else if (page === "contact") navigate("/contact");
        else if (page === "home") onBack();
        else if (page === "changelog") navigate("/changelog");
      })} />
    </div>
  );
}

export default RiskWarning;
